import React, { Dispatch } from 'react';
import { Box } from '@mui/material';
import { ISnackbarInput } from '../../Components/CustomUIElements/StyledSnackbar';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { callPaginatedAPI } from '../../Components/Utilities/callAPI';
import callAPI from '../../Components/Utilities/callAPI';
import queryString from 'query-string';
import {
  GenericTableAction,
  GenericTableState
} from '../../Components/GenericTable/genericTableReducer';
import { headerStyles } from './candidatePoolAssignmentConfig';
import CandidateGroupTableRows from './CandidateGroupTableRows';
import { IStyle } from '../../../ThemeContext/ThemeObject';
import GenericTable from '../../Components/GenericTable/GenericTable';
import SearchInput from '../../Components/GenericTable/SearchInput';
import { Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import TotalSelectedIndicatorWithActions from '../../Components/GenericTable/TotalSelectedIndicatorWithActions';

interface ICandidateSourcingPool {
  id: number;
  title: string;
  candidate_count: number;
}

interface CandidateGroup {
  id: number;
  name: string;
  position: string;
  location: string;
  emails: string[];
  phones: string[];
  contacts_revealed: boolean;
}

interface CandidateGroupsApiResponse {
  data: {
    candidate_groups: CandidateGroup[];
  };
  totalItems: number;
  totalPages: number;
}

const CandidateTab = ({
  isOpen,
  setSnackbarState,
  selectedPool,
  candidatePoolsState,
  candidatePoolsDispatch
}: {
  isOpen: boolean;
  setSnackbarState: (snackbarState: ISnackbarInput) => void;
  selectedPool: ICandidateSourcingPool;
  candidatePoolsState: GenericTableState;
  candidatePoolsDispatch: Dispatch<GenericTableAction>;
}) => {
  const queryClient = useQueryClient();

  const { data: candidateGroups, isFetching } = useQuery({
    queryKey: [
      'candidateGroups',
      selectedPool.id,
      candidatePoolsState.searchQuery,
      candidatePoolsState.searchSettings
    ],
    queryFn: () => {
      return callPaginatedAPI(
        `/api/v4/candidate_sourcing/candidate_pools/${selectedPool.id}/candidate_groups?${queryString.stringify(
          {
            ...candidatePoolsState.searchSettings,
            q: candidatePoolsState.searchQuery
          },
          {
            skipEmptyString: true,
            skipNull: true,
            arrayFormat: 'bracket',
            encode: false
          }
        )}`,
        null,
        'GET'
      ) as Promise<CandidateGroupsApiResponse>;
    },
    onError() {
      setSnackbarState({
        message: `There was an error retrieving candidate groups, please refresh the page and try again.`,
        state: 'error'
      });
    },
    enabled: isOpen
  });

  const { mutate: deleteCandidateGroups } = useMutation({
    mutationFn: (candidateGroupIds: number[]) =>
      callAPI(
        `/api/v4/candidate_sourcing/candidate_pools/${selectedPool.id}/remove_candidate_groups`,
        { candidate_group_ids: candidateGroupIds },
        'POST'
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidateGroups'] });
      queryClient.invalidateQueries({ queryKey: ['candidatePools'] });
      queryClient.invalidateQueries({ queryKey: ['candidatePool', selectedPool.id] });
      // Invalidate any queries that start with 'candidatePools' to ensure all related queries are refreshed
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'candidatePools'
      });
      // Force refetch the main candidate pools table to ensure fresh data
      queryClient.refetchQueries({ queryKey: ['candidatePools'] });
      setSnackbarState({
        message: 'Successfully removed candidate groups from the pool',
        state: 'success'
      });
    },
    onError() {
      setSnackbarState({
        message: 'There was an error removing the candidate groups, please try again.',
        state: 'error'
      });
    }
  });

  return (
    <Box>
      <SearchInput dispatch={candidatePoolsDispatch} id="search-box-candidate-tab" />
      {candidatePoolsState?.tableState ? (
        <>
          <GenericTable
            items={candidateGroups?.data?.candidate_groups || []}
            isLoadingItems={isFetching}
            dispatch={candidatePoolsDispatch}
            tableState={candidatePoolsState.tableState}
            paginationState={candidatePoolsState.searchSettings}
            pagination={{
              totalItems: candidateGroups?.totalItems || 0,
              totalPages: candidateGroups?.totalPages || 0
            }}
            headerStyle={headerStyles as (id: string, tableHovered: boolean) => IStyle}
            TableRows={CandidateGroupTableRows}
            TableActionList={[
              {
                id: 'delete',
                tooltipLabel: 'Delete',
                elementId: 'delete-individual-candidate-group-button',
                icon: <DeleteIcon />,
                actionCall: (item: any) => item?.id && deleteCandidateGroups([item.id])
              }
            ]}
            label="candidate groups"
            titleCellId="name"
            allowMultipleSelection={candidateGroups?.totalItems ? true : false}
            customEmptyStateMessage="available in this pool"
          />
          <TotalSelectedIndicatorWithActions
            totalSelected={candidatePoolsState.tableState.selectedItemIds.length}
            parentDiv={null}
            dispatch={candidatePoolsDispatch}
            actionButtonId="delete-candidate-group-action-button"
            actionButtonsList={[
              {
                id: 'delete',
                label: 'Delete',
                icon: <DeleteIcon />
              }
            ]}
            actionCall={() => {
              const selectedIds = candidatePoolsState.tableState.selectedItemIds;
              if (selectedIds.length > 0) {
                deleteCandidateGroups(selectedIds);
                candidatePoolsDispatch({
                  type: 'REMOVE_ALL_PAGE_ITEMS',
                  payload: selectedIds
                });
              }
            }}
          />
        </>
      ) : (
        <Box sx={{ padding: '20px', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading table configuration...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CandidateTab;
